import { memo } from "react";
import { ArrowLeft, Shield } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { Header } from "./Header";
import type { Screen } from "../types";

interface PrivacyPageProps {
  onBack: () => void;
  onNavigate?: (screen: Screen) => void;
}

export const PrivacyPage = memo(function PrivacyPage({
  onBack,
  onNavigate,
}: PrivacyPageProps) {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-slate-50 gradient-mesh">
      <Header onNavigate={onNavigate} showNav={false} />

      <main className="max-w-md mx-auto w-full px-4 py-6">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="mb-4 p-2 hover:bg-white/80 rounded-xl transition-smooth inline-flex items-center gap-2"
          aria-label="Back"
        >
          <ArrowLeft
            className={`w-5 h-5 text-slate-600 ${
              language === "ar" ? "rotate-180" : ""
            }`}
          />
          <span className="text-slate-600 font-medium">{t("common.back")}</span>
        </button>

        {/* Content */}
        <div className="bg-white rounded-3xl shadow-lg p-6 space-y-6">
          <h1 className="text-slate-900 font-semibold text-xl mb-6">
            {t("privacy.title")}
          </h1>

          <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3 text-primary">
              <Shield className="w-5 h-5" />
              <h2 className="text-base font-semibold text-slate-900">
                {t("privacy.intro")}
              </h2>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-2">
                {t("privacy.dataCollection")}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {t("privacy.dataCollectionText")}
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-2">
                {t("privacy.dataUse")}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {t("privacy.dataUseText")}
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-2">
                {t("privacy.dataSharing")}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {t("privacy.dataSharingText")}
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-2">
                {t("privacy.dataSecurity")}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {t("privacy.dataSecurityText")}
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-2">
                {t("privacy.yourRights")}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {t("privacy.yourRightsText")}
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-slate-900 mb-2">
                {t("privacy.changes")}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {t("privacy.changesText")}
              </p>
            </div>
          </div>

          {/* Developer Credit */}
          <div className="border-t border-slate-200 pt-6 mt-8">
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 text-center space-y-3">
              <p className="text-sm text-slate-600">
                {t("privacy.developedBy")}{" "}
                <a
                  href="https://rekcode.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark font-bold transition-smooth"
                >
                  rekcode.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
});
